import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import ProductPicker from '../../components/admin/ProductPicker';
import {
  API, withCreds, inputCls, selectCls, btnPrimary, btnSecondary, Field, Modal, PageHeader, Spinner,
  ScheduleBadge, discountLabel, fmtDateTime, toLocalInput, fromLocalInput, money, errMsg,
} from '../../components/admin/AdminUI';
import { Plus, Edit, Trash2, Boxes, Package, Link2 } from 'lucide-react';
import Select from '../../components/FlatSelect';

function DealModal({ deal, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', product_ids: [], discount_type: 'percent', discount_value: 10, starts_at: null, ends_at: null, active: true, ...(deal || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const body = { name: form.name, product_ids: form.product_ids, discount_type: form.discount_type, discount_value: form.discount_value,
      starts_at: form.starts_at, ends_at: form.ends_at, active: form.active };
    try {
      if (deal) await axios.put(`${API}/admin/bundle-deals/${deal.id}`, body, withCreds);
      else await axios.post(`${API}/admin/bundle-deals`, body, withCreds);
      onSaved();
    } catch (err) { setError(errMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={deal ? 'Edit buy-together deal' : 'New buy-together deal'} onClose={onClose} width="max-w-2xl">
      <form onSubmit={save} className="space-y-4">
        <Field label="Deal name *" hint="Shown to shoppers, e.g. 'Mug + coaster set'">
          <input value={form.name} onChange={e => set('name', e.target.value)} required className={inputCls} />
        </Field>
        <Field label="Products (2–6)" hint="Shoppers get the discount when all of these are in the cart, once per complete set">
          <ProductPicker value={form.product_ids} onChange={v => set('product_ids', v)} max={6} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Discount on the set">
            <div className="flex gap-2">
              <Select value={form.discount_type} onChange={e => set('discount_type', e.target.value)} className={selectCls}>
                <option value="percent">% off</option><option value="fixed">$ off</option>
              </Select>
              <input type="number" step="0.01" min="0" value={form.discount_value} onChange={e => set('discount_value', parseFloat(e.target.value) || 0)} className={inputCls} />
            </div>
          </Field>
          <label className="flex items-center gap-2 cursor-pointer self-end pb-3">
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="w-4 h-4 accent-honey" />
            <span className="text-ink/75 text-sm">Enabled</span>
          </label>
          <Field label="Starts" hint="Blank = now">
            <input type="datetime-local" value={toLocalInput(form.starts_at)} onChange={e => set('starts_at', fromLocalInput(e.target.value))} className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
          <Field label="Ends" hint="Blank = no end">
            <input type="datetime-local" value={toLocalInput(form.ends_at)} onChange={e => set('ends_at', fromLocalInput(e.target.value))} className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
        </div>
        <p className="text-ink/45 text-xs">The deal also appears as "Frequently bought together" on each product's page, with an add-all button.</p>
        {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>{saving ? 'Saving…' : 'Save deal'}</button>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminBundles() {
  const [tab, setTab] = useState('deals');
  const [deals, setDeals] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [d, b] = await Promise.all([
        axios.get(`${API}/admin/bundle-deals`, withCreds),
        axios.get(`${API}/admin/products`, { params: { status: 'bundle', limit: 200 }, ...withCreds }),
      ]);
      setDeals(d.data.deals); setBundles(b.data.products);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(`Delete the deal "${d.name}"?`)) return;
    await axios.delete(`${API}/admin/bundle-deals/${d.id}`, withCreds);
    load();
  };

  return (
    <AdminLayout>
      {editing && <DealModal deal={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <PageHeader title="Bundles & Deals" subtitle="Sell items together for less">
        {tab === 'deals'
          ? <button onClick={() => setEditing('new')} className={btnPrimary}><Plus className="w-4 h-4" /> New deal</button>
          : <Link to="/admin/products?new=bundle" className={btnPrimary}><Plus className="w-4 h-4" /> New bundle listing</Link>}
      </PageHeader>

      <div className="flex gap-2 mb-4">
        {[['deals', `Buy-together deals (${deals.length})`, Link2], ['bundles', `Bundle listings (${bundles.length})`, Boxes]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border ${tab === k ? 'bg-honey/10 text-honey-700 border-honey/30' : 'bg-ink/[0.04] text-ink/60 border-ink/10'}`}>
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {loading ? <Spinner /> : tab === 'deals' ? (
        <div className="space-y-3">
          <p className="text-ink/55 text-sm">Automatic cart discounts when shoppers buy specific items together. Nothing new to list or stock.</p>
          {deals.length === 0 && <div className="admin-card rounded-xl text-center py-12 text-ink/45 text-sm">No deals yet.</div>}
          {deals.map(d => {
            const value = d.products.reduce((s, p) => s + (p.price || 0), 0);
            const price = d.discount_type === 'percent' ? value * (1 - d.discount_value / 100) : Math.max(0, value - d.discount_value);
            return (
              <div key={d.id} className="admin-card rounded-xl p-4 flex flex-wrap items-center gap-4">
                <div className="flex -space-x-3">
                  {d.products.map(p => (
                    <div key={p.id} className="w-12 h-12 rounded-lg overflow-hidden bg-ink/[0.04] border-2 border-white" title={p.title}>
                      {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-ink/30 m-auto mt-3" />}
                    </div>
                  ))}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-ink font-medium">{d.name} <span className="text-honey-600 text-sm ml-2">{discountLabel(d.discount_type, d.discount_value)}</span></p>
                  <p className="text-ink/55 text-xs">{d.products.map(p => p.title + (p.published ? '' : ' (unpublished)')).join(' + ')}</p>
                  <p className="text-ink/55 text-xs">Set: <span className="line-through">{money(value)}</span> → <span className="text-ink">{money(price)}</span> at regular prices
                    {d.ends_at ? ` · ends ${fmtDateTime(d.ends_at)}` : ''}</p>
                </div>
                <ScheduleBadge status={d.status} />
                <button onClick={() => setEditing({ ...d, product_ids: d.product_ids })} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>
                <button onClick={() => remove(d)} className="text-ink/55 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-ink/55 text-sm">A bundle is its own product with its own price and photos. Stock comes from the items inside, so it shows sold out when any piece runs out.</p>
          <div className="admin-card rounded-xl overflow-x-auto">
            {bundles.length === 0 ? <div className="text-center py-12 text-ink/45 text-sm">No bundle listings yet.</div> : (
              <table className="w-full text-sm">
                <thead className="border-b border-ink/10">
                  <tr className="text-ink/55 text-xs uppercase tracking-widest"><th className="text-left p-3">Bundle</th><th className="text-left p-3">Contains</th><th className="text-left p-3">Price</th><th className="text-left p-3">Status</th><th className="p-3" /></tr>
                </thead>
                <tbody className="divide-y divide-ink/10">
                  {bundles.map(b => (
                    <tr key={b.id} className="text-ink/75 hover:bg-ink/[0.04]">
                      <td className="p-3 text-ink font-medium">{b.title}</td>
                      <td className="p-3 text-xs">{b.bundle_items.reduce((n, i) => n + i.quantity, 0)} item(s)</td>
                      <td className="p-3 font-bold text-ink">{money(b.price)}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full font-bold ${b.published ? 'bg-green-500/10 text-green-600' : 'bg-ink/[0.04] text-ink/45'}`}>{b.published ? 'Live' : 'Draft'}</span></td>
                      <td className="p-3 text-right"><Link to={`/admin/products?edit=${b.id}`} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4 inline" /></Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
